import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { Shape, Tool, CanvasProps } from '../types/Shape';
import { getConnectionPoints, findNearestConnectionPoint, findShapeAtPoint, getRelativePosition, getAbsolutePosition } from '../utils/connectionUtils';
import { updateArrowPosition, updateArrowConnections } from '../utils/arrowUtils';
import { calculateSnapPosition, AlignmentGuide } from '../utils/snapUtils';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useClipboard } from '../hooks/useClipboard';
import { useCanvasTools } from '../hooks/useCanvasTools';
import { useCanvasViewport } from '../hooks/useCanvasViewport';
import { useCanvasState } from '../hooks/useCanvasState';
import { useCanvasHistory } from '../hooks/useCanvasHistory';
import { useCanvasSelection } from '../hooks/useCanvasSelection';
import { useCanvasEditing } from '../hooks/useCanvasEditing';
import { useCanvasArrows } from '../hooks/useCanvasArrows';
import { useCanvasInteractions } from '../hooks/useCanvasInteractions';
import { useCanvasMouseDown } from '../hooks/useCanvasMouseDown';
import { useCanvasMouseMove } from '../hooks/useCanvasMouseMove';
import { useCanvasMouseUp } from '../hooks/useCanvasMouseUp';
import { useCanvasTouch } from '../hooks/useCanvasTouch';
import { useShapeHandlers } from '../hooks/useShapeHandlers';
import { useArrowHandlers } from '../hooks/useArrowHandlers';
import { useCanvasDropHandlers } from '../hooks/useCanvasDropHandlers';
import { pdfjsLib } from '../utils/pdfConfig';
import Toolbar from './Toolbar';
import PDFViewer from './PDFViewer';
import Minimap from './Minimap';
import HelpPanel from './HelpPanel';
import CodeEditorModal from './CodeEditorModal';
import MarkdownEditorModal from './MarkdownEditorModal';
import ExportModal from './ExportModal';
import ImageConcatModal from './ImageConcatModal';
import { useExport } from '../hooks/useExport';

import HTMLRectangle from './html-shapes/HTMLRectangle';
import HTMLCircle from './html-shapes/HTMLCircle';
import SVGArrow from './html-shapes/SVGArrow';
import HTMLPDF from './html-shapes/HTMLPDF';
import HTMLImage from './html-shapes/HTMLImage';
import HTMLCodeBlock from './html-shapes/HTMLCodeBlock';
import HTMLMarkdown from './html-shapes/HTMLMarkdown';
import HTMLYoutube from './html-shapes/HTMLYoutube';
import HTMLAudio from './html-shapes/HTMLAudio';
import ConnectionPoints from './html-shapes/ConnectionPoints';
import AlignmentGuides from './AlignmentGuides';
import { ContextMenuPanel } from './canvas/ContextMenuPanel';
import { InlineEditorOverlay } from './canvas/InlineEditorOverlay';
import { UserCursors } from './collaboration/UserCursor';
import { SelectionOverlay } from './collaboration/SelectionOverlay';
import { CollaborationPanel } from './collaboration/CollaborationPanel';
import { useCursorSync } from '../hooks/useCursorSync';
import { useCollaboration } from '../contexts/CollaborationContext';
import { useI18n } from '../contexts/I18nContext';
import { applyRemoteChanges, calculateDiff, createUpdatePayload, serializeShapes } from '../utils/syncUtils';
import '../styles/components/Canvas.css';

const Canvas: React.FC<CanvasProps> = ({ width, height }) => {
  const { t } = useI18n();
  const overlayZIndex = 9999;
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  // Phase 2 Hooks - State management (must come first as other hooks depend on shapes)
  const {
    shapes,
    setShapes,
    shapesRef,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    history,
    updateConnectedArrows,
    handleSave,
    handleLoad,
    handleObjectUpdate: stateHandleObjectUpdate,
    handleShapeUpdate,
    handleExtractPage,
    handleAnnotationUpdate,
  } = useCanvasState();

  // Phase 1 Hooks
  const {
    currentTool,
    setCurrentTool,
    arrowDefaults,
    setDefaultStartArrow,
    setDefaultEndArrow,
    setDefaultIsCurved,
    setDefaultArrowStroke,
    setDefaultArrowStrokeWidth,
    shapeDefaults,
    setDefaultFill,
    setDefaultStroke,
    setDefaultStrokeWidth,
    setDefaultFontSize,
    showConnectionPoints,
    setShowConnectionPoints,
    handleToolChange: toolChangeHandler,
  } = useCanvasTools();

  const {
    stagePos,
    setStagePos,
    stageScale,
    setStageScale,
    canvasSize,
    canvasBounds,
    isCanvasDragging,
    setIsCanvasDragging,
    isWheelPanning,
    setIsWheelPanning,
    canvasDragStart,
    setCanvasDragStart,
    canvasDragRafRef,
    lastTouchDistanceRef,
    lastTouchCenterRef,
    lastSingleTouchPosRef,
    isTouchPanningRef,
    handleWheel,
    handleStageDoubleClick,
    handleCanvasMouseLeave,
    screenToCanvas,
  } = useCanvasViewport({ width, height, shapes, canvasContainerRef });

  // Phase 3 Hooks - Selection & Editing
  const {
    selectedShapes,
    setSelectedShapes,
    showResizeHandles,
    setShowResizeHandles,
    isLassoSelecting,
    setIsLassoSelecting,
    lassoStart,
    setLassoStart,
    lassoEnd,
    setLassoEnd,
    copiedShapes,
    setCopiedShapes,
    contextMenu,
    setContextMenu,
    draggingShapeId,
    setDraggingShapeId,
    selectedShapesStartPositionsRef,
  } = useCanvasSelection();

  const {
    editingShape,
    setEditingShape,
    editingText,
    setEditingText,
    editingPosition,
    setEditingPosition,
    editingMarkdown,
    setEditingMarkdown,
    editingMarkdownPosition,
    setEditingMarkdownPosition,
    editingCode,
    setEditingCode,
    editingCodeLanguage,
    setEditingCodeLanguage,
    editingCodePosition,
    setEditingCodePosition,
    isCodeEditorOpen,
    setIsCodeEditorOpen,
    editingCodeShapeId,
    setEditingCodeShapeId,
    isMarkdownEditorOpen,
    setIsMarkdownEditorOpen,
    editingMarkdownShapeId,
    setEditingMarkdownShapeId,
    handleTextEditComplete,
    handleTextEditCancel,
    handleCodeSave,
    handleCodeEditorClose,
    handleMarkdownSave,
    handleMarkdownEditorClose,
    handleDoubleClickToEdit,
  } = useCanvasEditing({ shapes, setShapes, setHasUnsavedChanges, stagePos, stageScale, canvasContainerRef });

  // Phase 2 Hook - History (Undo/Redo)
  const {
    handleUndo,
    handleRedo,
    handleBeforeAction,
    isUserActionRef: isUserAction,
    hasActionStartedRef: hasActionStarted,
    markActionComplete,
  } = useCanvasHistory({ shapes, setShapes, history });

  // Phase 4 Hooks - Arrows & Interactions
  const {
    isConnecting,
    setIsConnecting,
    snappedPoint,
    setSnappedPoint,
    hoveredShapeId,
    setHoveredShapeId,
    isDraggingArrowEndpoint,
    setIsDraggingArrowEndpoint,
    mousePos,
    setMousePos,
    nearbyShapeIds,
    setNearbyShapeIds,
    nearbyShapeIdsRef,
  } = useCanvasArrows();

  const {
    isDrawing,
    setIsDrawing,
    currentShape,
    setCurrentShape,
    isResizing,
    setIsResizing,
    resizeHandle,
    setResizeHandle,
    resizeStartPos,
    setResizeStartPos,
    resizeStartShape,
    setResizeStartShape,
    alignmentGuides,
    setAlignmentGuides,
    snapCalculationRafRef,
    pendingSnapCalculationRef,
    lastSnappedPositionRef,
    showPDFViewer,
    setShowPDFViewer,
    toolbarWidth,
    setToolbarWidth,
    clearDrawingState,
  } = useCanvasInteractions();

  // Destructure defaults for easier access
  const { startArrow: defaultStartArrow, endArrow: defaultEndArrow, isCurved: defaultIsCurved, stroke: defaultArrowStroke, strokeWidth: defaultArrowStrokeWidth } = arrowDefaults;
  const { fill: defaultFill, stroke: defaultStroke, strokeWidth: defaultStrokeWidth, fontSize: defaultFontSize } = shapeDefaults;

  // Remaining local state
  const [pdfFile] = useState<File | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);

  // Image Concat Modal state
  const [isImageConcatModalOpen, setIsImageConcatModalOpen] = useState(false);

  // Export hook
  const {
    isExportModalOpen,
    exportProgress,
    openExportModal,
    closeExportModal,
    handleExport,
    quickExportPDF,
    quickExportJPG,
  } = useExport({
    shapes,
    selectedShapes,
    stagePos,
    stageScale,
    viewportWidth: width,
    viewportHeight: height,
    canvasContainerRef,
  });

  // Collaboration context for mode check and selection sync
  const { state: collaborationState, sendSelection, sendUpdate, lockedShapes, remoteShapes, remoteUpdate, isLeavingSession } = useCollaboration();
  const isCollaborativeMode = collaborationState.mode === 'collaborative';

  // Cursor sync hook (only active in collaborative mode)
  const {
    remoteCursors,
    handleMouseMove: handleCursorMove,
    handleMouseLeave: handleCursorLeave,
  } = useCursorSync({
    enabled: isCollaborativeMode,
    stagePos,
    stageScale,
    canvasContainerRef,
  });

  // Sync selection state in collaborative mode
  useEffect(() => {
    if (isCollaborativeMode) {
      sendSelection(selectedShapes);
    }
  }, [isCollaborativeMode, selectedShapes, sendSelection]);

  // 세션 종료 시 처리: 자발적으로 나간 경우에만 객체 지우기
  const prevSessionRef = useRef<string | null>(null);
  useEffect(() => {
    const currentSessionId = collaborationState.session?.id || null;
    const prevSessionId = prevSessionRef.current;

    // 세션이 종료된 경우 (이전에는 세션이 있었지만 지금은 없음)
    if (prevSessionId !== null && currentSessionId === null) {
      // 자발적으로 나간 경우에만 객체를 모두 지움
      if (isLeavingSession()) {
        setShapes([]);
        setSelectedShapes([]);
      }
      // 드로잉 상태는 항상 초기화
      clearDrawingState();
    }

    prevSessionRef.current = currentSessionId;
  }, [collaborationState.session?.id, isLeavingSession, clearDrawingState, setShapes, setSelectedShapes]);

  // Sync shapes in collaborative mode using incremental updates
  // Track previous shapes using usePrevious pattern (updates AFTER render, not during)
  const prevShapesRef = useRef<Shape[]>([]);
  const isFromRemoteRef = useRef<boolean>(false);
  const sendUpdateRef = useRef(sendUpdate);

  // Keep sendUpdate ref up to date
  useEffect(() => {
    sendUpdateRef.current = sendUpdate;
  }, [sendUpdate]);

  // Throttle refs for sync updates
  const lastSyncTimeRef = useRef<number>(0);
  const pendingSyncRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const SYNC_THROTTLE_FAST_MS = 50; // For annotations - max 20 updates per second

  // Send local changes to server (incremental diff)
  // Skip sync during drag - only sync on mouse up (drag end)
  useEffect(() => {
    if (!isCollaborativeMode) return;

    // Skip sync while dragging - will sync on drag end
    if (draggingShapeId !== null) return;

    const prevShapes = prevShapesRef.current;

    // Skip if this change came from remote
    if (isFromRemoteRef.current) {
      isFromRemoteRef.current = false;
      prevShapesRef.current = shapes;
      return;
    }

    // Calculate diff
    const diff = calculateDiff(prevShapes, shapes);

    if (!diff.hasChanges) {
      prevShapesRef.current = shapes;
      return;
    }

    // Check if this is an annotation change (needs fast sync)
    const isAnnotationChange = diff.updated.some(shape => {
      const prevShape = prevShapes.find(s => s.id === shape.id);
      if (!prevShape) return false;
      // Check if annotations array changed (reference comparison)
      return prevShape.annotations !== shape.annotations;
    });

    // Use fast throttle for annotations, immediate for drag end
    const throttleMs = isAnnotationChange ? SYNC_THROTTLE_FAST_MS : 0;

    // Update ref BEFORE sending
    prevShapesRef.current = shapes;

    // Send function
    const sendSync = () => {
      const payload = createUpdatePayload(diff);
      if (payload) {
        sendUpdateRef.current({
          action: payload.action,
          shapes: payload.shapes,
          shapeIds: payload.shapeIds,
        });
      }
      lastSyncTimeRef.current = Date.now();
    };

    const now = Date.now();
    const timeSinceLastSync = now - lastSyncTimeRef.current;

    // Clear any pending sync
    if (pendingSyncRef.current) {
      clearTimeout(pendingSyncRef.current);
      pendingSyncRef.current = null;
    }

    if (throttleMs === 0 || timeSinceLastSync >= throttleMs) {
      // Send immediately (drag end or enough time passed)
      sendSync();
    } else {
      // Schedule sync for later (ensures last update is always sent)
      pendingSyncRef.current = setTimeout(() => {
        sendSync();
        pendingSyncRef.current = null;
      }, throttleMs - timeSinceLastSync);
    }
  }, [isCollaborativeMode, shapes, draggingShapeId]);

  // Cleanup pending sync on unmount
  useEffect(() => {
    return () => {
      if (pendingSyncRef.current) {
        clearTimeout(pendingSyncRef.current);
      }
    };
  }, []);

  // Apply remote shapes from other users (full sync - bulk action)
  const lastRemoteShapesJsonRef = useRef<string>('');
  useEffect(() => {
    if (!isCollaborativeMode || !remoteShapes) return;

    // Deduplicate by JSON comparison
    const remoteJson = JSON.stringify(remoteShapes);
    if (remoteJson === lastRemoteShapesJsonRef.current) return;
    lastRemoteShapesJsonRef.current = remoteJson;

    // Mark as remote update to prevent echo
    isFromRemoteRef.current = true;
    prevShapesRef.current = remoteShapes as Shape[];
    setShapes(remoteShapes as Shape[]);
  }, [isCollaborativeMode, remoteShapes, setShapes]);

  // Apply remote incremental updates (add, update, delete actions)
  const lastRemoteUpdateIdRef = useRef<number>(0);
  useEffect(() => {
    if (!isCollaborativeMode || !remoteUpdate) return;
    // Skip already processed updates
    if (remoteUpdate.updateId <= lastRemoteUpdateIdRef.current) return;
    lastRemoteUpdateIdRef.current = remoteUpdate.updateId;

    // Skip bulk updates - they are handled by remoteShapes
    if (remoteUpdate.action === 'bulk') return;

    // Mark as remote update to prevent echo
    isFromRemoteRef.current = true;

    // Apply incremental update
    setShapes(prev => {
      const updatedShapes = applyRemoteChanges(
        prev,
        remoteUpdate.action as 'add' | 'update' | 'delete' | 'bulk',
        {
          shapes: remoteUpdate.shapes,
          shapeIds: remoteUpdate.shapeIds,
        }
      );

      // Update prevShapesRef to match new state
      prevShapesRef.current = updatedShapes;

      return updatedShapes;
    });
  }, [isCollaborativeMode, remoteUpdate, setShapes]);

  // Phase 2-1 Hooks - Event Handlers
  const { handleStageMouseDown } = useCanvasMouseDown({
    currentTool,
    shapes,
    stageScale,
    isConnecting,
    editingShape,
    defaultStartArrow,
    defaultEndArrow,
    defaultIsCurved,
    defaultArrowStroke,
    defaultArrowStrokeWidth,
    defaultFill,
    defaultStroke,
    defaultStrokeWidth,
    defaultFontSize,
    setIsCanvasDragging,
    setIsWheelPanning,
    setCanvasDragStart,
    setShowResizeHandles,
    setSelectedShapes,
    setIsLassoSelecting,
    setLassoStart,
    setLassoEnd,
    setIsConnecting,
    setShowConnectionPoints,
    setCurrentShape,
    setIsDrawing,
    handleTextEditComplete,
    history,
    hasActionStarted,
  });

  const { handleStageMouseMove } = useCanvasMouseMove({
    currentTool,
    shapes,
    stagePos,
    stageScale,
    isDrawing,
    currentShape,
    isResizing,
    resizeHandle,
    resizeStartPos,
    resizeStartShape,
    selectedShapes,
    isCanvasDragging,
    isWheelPanning,
    canvasDragStart,
    isConnecting,
    isDraggingArrowEndpoint,
    isLassoSelecting,
    lassoStart,
    editingShape,
    editingMarkdownPosition,
    editingCodePosition,
    canvasContainerRef,
    canvasDragRafRef,
    setMousePos,
    setNearbyShapeIds,
    setLassoEnd,
    setStagePos,
    setCanvasDragStart,
    setIsCanvasDragging,
    setSnappedPoint,
    setCurrentShape,
    setShapes,
    setEditingMarkdownPosition,
    setEditingCodePosition,
  });

  const { handleStageMouseUp } = useCanvasMouseUp({
    shapes,
    stagePos,
    stageScale,
    isDrawing,
    currentShape,
    isResizing,
    isCanvasDragging,
    isWheelPanning,
    isConnecting,
    isLassoSelecting,
    lassoStart,
    lassoEnd,
    defaultStartArrow,
    defaultEndArrow,
    canvasDragRafRef,
    hasActionStarted,
    setIsWheelPanning,
    setIsCanvasDragging,
    setCanvasDragStart,
    setSelectedShapes,
    setShowResizeHandles,
    setIsLassoSelecting,
    setLassoStart,
    setLassoEnd,
    setIsResizing,
    setResizeHandle,
    setResizeStartPos,
    setResizeStartShape,
    setShowConnectionPoints,
    setSnappedPoint,
    setCurrentShape,
    setIsConnecting,
    setShapes,
    setCurrentTool,
    setIsDrawing,
    setHasUnsavedChanges,
  });

  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useCanvasTouch({
    stagePos,
    stageScale,
    canvasContainerRef,
    isTouchPanningRef,
    lastTouchDistanceRef,
    lastTouchCenterRef,
    lastSingleTouchPosRef,
    setStagePos,
    setStageScale,
    handleStageMouseDown,
    handleStageMouseMove,
    handleStageMouseUp,
  });

  // Shape drag/resize/click handlers
  const {
    handleDragPrepare,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleShapeResizeStart,
    handleShapeResize,
    handleShapeResizeEnd,
    handleShapeClick,
  } = useShapeHandlers({
    shapes,
    shapesRef,
    selectedShapes,
    stageScale,
    history,
    hasActionStarted,
    selectedShapesStartPositionsRef,
    lastSnappedPositionRef,
    snapCalculationRafRef,
    pendingSnapCalculationRef,
    setDraggingShapeId,
    setSelectedShapes,
    setShowResizeHandles,
    setAlignmentGuides,
    setShapes,
    setHasUnsavedChanges,
  });

  // Arrow handlers
  const {
    handleArrowStageClick,
    handleStartPointMove,
    handleEndPointMove,
    handleStartPointEnd,
    handleEndPointEnd,
    handleArrowContextMenu,
    handleArrowDragStart,
    handleArrowDragMove,
    handleArrowDragEnd,
    handleSnapUpdate,
    handleEndpointDraggingChange,
    handleCurveControlMove,
    handleWaypointMove,
    handleWaypointAdd,
    handleWaypointDelete,
  } = useArrowHandlers({
    shapes,
    setShapes,
    stageScale,
    currentShape,
    setCurrentShape,
    isConnecting,
    setIsConnecting,
    setShowConnectionPoints,
    setSnappedPoint,
    setCurrentTool,
    setHasUnsavedChanges,
    defaultArrowStroke,
    defaultArrowStrokeWidth,
    defaultStartArrow,
    defaultEndArrow,
    currentTool,
    setContextMenu,
    setIsDraggingArrowEndpoint,
    pushHistory: history.pushHistory,
    hasActionStarted,
  });

  // Drop handlers for files
  const {
    handleDragOver,
    handleDragLeave,
    handleDrop,
    isDragOver,
  } = useCanvasDropHandlers({
    shapes,
    setShapes,
    setSelectedShapes,
    setShowResizeHandles,
    stagePos,
    stageScale,
    canvasContainerRef,
  });

  // Helper function to update editor position when dragging/resizing an editing shape
  const updateEditorPosition = useCallback((shapeId: string, newX: number, newY: number, newWidth?: number, newHeight?: number) => {
    if (editingShape !== shapeId || !canvasContainerRef.current) return;

    const shape = shapes.find(s => s.id === shapeId);
    if (!shape) return;

    const canvasBox = canvasContainerRef.current.getBoundingClientRect();
    const padding = shape.type === 'code' ? 0 : 8;
    const screenX = newX * stageScale + stagePos.x;
    const screenY = newY * stageScale + stagePos.y;
    const width = newWidth ?? shape.width ?? 100;
    const height = newHeight ?? shape.height ?? (shape.radius ? shape.radius * 2 : 100);
    const screenWidth = width * stageScale;
    const screenHeight = height * stageScale;

    const newPos = {
      x: canvasBox.left + screenX + (padding * stageScale),
      y: canvasBox.top + screenY + (padding * stageScale),
      width: screenWidth - (padding * 2 * stageScale),
      height: screenHeight - (padding * 2 * stageScale)
    };

    if (shape.type === 'code') {
      setEditingCodePosition(newPos);
    } else if (shape.type === 'md') {
      setEditingMarkdownPosition(newPos);
    } else {
      setEditingPosition(newPos);
    }
  }, [editingShape, shapes, stageScale, stagePos, setEditingPosition, setEditingCodePosition, setEditingMarkdownPosition]);

  // canvas-container style memoization
  const canvasContainerStyle = useMemo(() => {
    const cursor = currentTool === 'arrow' ? 'crosshair' : 'default';

    return {
      cursor,
      ...(isDragOver ? {
        outline: '3px dashed #3b82f6',
        outlineOffset: '-3px',
        backgroundColor: 'rgba(59, 130, 246, 0.04)',
      } : {}),
    };
  }, [currentTool, isDragOver]);

  const canvasContainerClassName = useMemo(() =>
    `canvas-container ${isCanvasDragging ? 'dragging' : ''}`,
    [isCanvasDragging]
  );

  // 화살표 초기화 및 order 설정은 useCanvasState 훅에서 처리

  // 컨텍스트 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenu) {
        setContextMenu(null);
      }
    };

    if (contextMenu) {
      window.addEventListener('click', handleClickOutside);
      return () => window.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  // handleUndo, handleRedo, handleBeforeAction은 useCanvasHistory 훅에서 제공

  // 키보드 단축키
  useKeyboardShortcuts({
    selectedShapes,
    editingShape,
    shapes,
    setShapes,
    setSelectedShapes,
    setShowResizeHandles,
    setShowConnectionPoints,
    copiedShapes,
    setCopiedShapes,
    onUndo: handleUndo,
    onRedo: handleRedo,
    isUserActionRef: isUserAction,
    onBeforeAction: handleBeforeAction,
    onExport: openExportModal,
    onQuickExportPDF: quickExportPDF,
    onQuickExportJPG: quickExportJPG,
  });

  // 클립보드 이벤트
  useClipboard({
    editingShape,
    stagePos,
    stageScale,
    width,
    height,
    setShapes,
    copiedShapes,
    selectedShapes,
    setSelectedShapes,
    setShowResizeHandles,
  });

  // updateConnectedArrows는 useCanvasState 훅에서 제공
  // handleTextEditComplete, handleTextEditCancel, handleCodeSave, handleCodeEditorClose,
  // handleMarkdownSave, handleMarkdownEditorClose는 useCanvasEditing 훅에서 제공

  // handleObjectUpdate: 컴포넌트의 selectedShapes를 자동으로 전달하는 래퍼
  const handleObjectUpdate = useCallback((updates: Partial<Shape>) => {
    stateHandleObjectUpdate(updates, selectedShapes);
  }, [selectedShapes, stateHandleObjectUpdate]);

  // handleShapeUpdate, handleExtractPage는 useCanvasState 훅에서 제공


  const handleResizeStart = useCallback((shapeId: string, handle: string, e: any) => {
    e.cancelBubble = true;

    // 휠 버튼으로 리사이즈 시작한 경우 무시
    if (e.evt.button === 1) {
      return;
    }

    // 다중 선택된 경우 리사이즈 비활성화
    if (selectedShapes.length > 1) {
      return;
    }

    const shape = shapes.find(s => s.id === shapeId);
    if (shape && selectedShapes.includes(shapeId)) {
      const stage = e.target.getStage();
      if (!stage) return;

      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      // 리사이즈 시작 시 현재 상태를 히스토리에 저장
      if (!hasActionStarted.current) {
        history.pushHistory(shapes);
        hasActionStarted.current = true;
      }

      setIsResizing(true);
      setResizeHandle(handle);
      // Stage 좌표계 기준으로 저장
      setResizeStartPos({ x: pointer.x, y: pointer.y });
      setResizeStartShape({
        x: shape.x,
        y: shape.y,
        width: shape.width || 0,
        height: shape.height || 0
      });

      // 화살표 리사이즈 시작 시 연결점 표시
      if (shape.type === 'arrow') {
        setShowConnectionPoints(true);
      }
    }
  }, [shapes, selectedShapes, history]);

  // handleSave, handleLoad는 useCanvasState 훅에서 제공

  // 이미지 연결 핸들러
  const handleImageConcatConfirm = useCallback((
    concatenatedImageDataUrl: string,
    imgWidth: number,
    imgHeight: number
  ) => {
    // 캔버스 중앙에 이미지 배치
    const centerX = (-stagePos.x + width / 2) / stageScale - imgWidth / 2;
    const centerY = (-stagePos.y + height / 2) / stageScale - imgHeight / 2;

    setShapes((prev: Shape[]) => {
      const maxOrder = Math.max(...prev.map((s: Shape) => s.order ?? 0), 0);
      const newOrder = maxOrder % 2 === 0 ? maxOrder + 2 : maxOrder + 1;

      const newImageShape: Shape = {
        id: Date.now().toString(),
        type: 'image',
        x: centerX,
        y: centerY,
        width: imgWidth,
        height: imgHeight,
        imageDataUrl: concatenatedImageDataUrl,
        stroke: '#D1D5DB',
        strokeWidth: 1,
        order: newOrder,
        renderMode: 'html',
      };

      // 새로 생성된 이미지 선택
      setSelectedShapes([newImageShape.id]);
      setShowResizeHandles(true);

      return [...prev, newImageShape];
    });
  }, [stagePos, stageScale, width, height, setShapes, setSelectedShapes, setShowResizeHandles]);

  const handleResizeEnd = useCallback(() => {
    // 리사이즈 중인 도형이 있다면 연결된 화살표 동기화 (화살표 자체는 제외)
    if (selectedShapes.length > 0) {
      const shape = shapes.find(s => s.id === selectedShapes[0]);
      if (shape && shape.type !== 'arrow') {
        updateConnectedArrows(selectedShapes[0]);
      }
    }

    setIsResizing(false);
    setResizeHandle(null);
    setResizeStartPos(null);
    setResizeStartShape(null);
    hasActionStarted.current = false;

    // 화살표 리사이즈 종료 시 연결점 숨김 및 스냅 포인트 초기화
    setShowConnectionPoints(false);
    setSnappedPoint(null);
  }, [selectedShapes, updateConnectedArrows, shapes]);

  // handleStageMouseDown is provided by useCanvasMouseDown hook

  // handleStageMouseMove is provided by useCanvasMouseMove hook

  // handleStageMouseUp is provided by useCanvasMouseUp hook

  // handleTouchStart, handleTouchMove, handleTouchEnd are provided by useCanvasTouch hook
  // handleWheel, handleStageDoubleClick are provided by useCanvasViewport hook



  // handleToolChange는 useCanvasTools 훅의 toolChangeHandler 사용
  const handleToolChange = useCallback((tool: Tool) => {
    toolChangeHandler(tool, () => setShowResizeHandles(false));
  }, [toolChangeHandler]);

  // 툴바 너비 측정
  useEffect(() => {
    if (!toolbarRef.current) return;

    const updateToolbarWidth = () => {
      if (toolbarRef.current) {
        const rect = toolbarRef.current.getBoundingClientRect();
        setToolbarWidth(rect.width);
      }
    };

    // 초기 측정
    updateToolbarWidth();

    // ResizeObserver로 툴바 크기 변화 감지
    const resizeObserver = new ResizeObserver(() => {
      updateToolbarWidth();
    });

    resizeObserver.observe(toolbarRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);



  return (
    <>
      <div
        ref={canvasContainerRef}
        className={canvasContainerClassName}
        style={{ ...canvasContainerStyle, touchAction: 'none' }} // touchAction none 중요
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={(e: React.MouseEvent) => {
          // 마우스 휠 버튼(중간 버튼)으로 캔버스 드래그 시작
          if (e.button === 1) {
            e.preventDefault();
            setIsWheelPanning(true);
            setIsCanvasDragging(true);
            setCanvasDragStart({ x: e.clientX, y: e.clientY });
            return;
          }

          // 배경 클릭 시 처리 (이벤트가 shape에서 전파되지 않은 경우)
          if (e.target === canvasContainerRef.current && currentTool === 'select') {
            // 왼쪽 버튼만 올가미 선택
            if (e.button === 0) {
              setSelectedShapes([]);
              setShowResizeHandles(false);

              // 올가미 선택 시작
              const rect = canvasContainerRef.current?.getBoundingClientRect();
              if (rect) {
                const canvasX = (e.clientX - rect.left - stagePos.x) / stageScale;
                const canvasY = (e.clientY - rect.top - stagePos.y) / stageScale;

                setIsLassoSelecting(true);
                setLassoStart({ x: canvasX, y: canvasY });
                setLassoEnd({ x: canvasX, y: canvasY });
              }
            }
          }

          // 기존 Konva 이벤트 핸들링
          const rect = canvasContainerRef.current?.getBoundingClientRect();
          if (!rect) return;

          // 가짜 Konva 이벤트 객체 생성
          const fakeEvent = {
            evt: e.nativeEvent,
            cancelBubble: false,
            target: {
              getStage: () => ({
                getPointerPosition: () => ({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top
                }),
                scaleX: () => stageScale,
                scaleY: () => stageScale,
                x: () => stagePos.x,
                y: () => stagePos.y,
                container: () => canvasContainerRef.current
              }),
              getParent: () => null
            }
          };
          handleStageMouseDown(fakeEvent);
        }}
        onMouseMoveCapture={(e: React.MouseEvent) => {
          // Cursor sync for collaborative mode (throttled internally)
          if (isCollaborativeMode) {
            handleCursorMove(e);
          }

          const rect = canvasContainerRef.current?.getBoundingClientRect();
          if (!rect) return;

          // 휠 버튼으로 캔버스 드래그 중이면 캔버스 이동 처리
          if (isCanvasDragging && isWheelPanning && canvasDragStart) {
            const deltaX = e.clientX - canvasDragStart.x;
            const deltaY = e.clientY - canvasDragStart.y;

            // RAF로 성능 최적화
            if (canvasDragRafRef.current !== null) {
              cancelAnimationFrame(canvasDragRafRef.current);
            }

            canvasDragRafRef.current = requestAnimationFrame(() => {
              setStagePos(prev => ({
                x: prev.x + deltaX,
                y: prev.y + deltaY
              }));
              setCanvasDragStart({ x: e.clientX, y: e.clientY });
            });
            return;
          }

          // 현재 마우스 위치 계산 (캡처 단계에서 처리하여 HTML shapes 위에서도 작동)
          const canvasX = (e.clientX - rect.left - stagePos.x) / stageScale;
          const canvasY = (e.clientY - rect.top - stagePos.y) / stageScale;

          // 올가미 선택 중이면 끝점 업데이트
          if (isLassoSelecting && lassoStart) {
            setLassoEnd({ x: canvasX, y: canvasY });
          }

          // 화살표 도구일 때 근처 shape 감지 (객체 둘레 기준)
          if (currentTool === 'arrow' && !isConnecting && !isDraggingArrowEndpoint) {
            const proximityMargin = 50; // 둘레에서 정확히 50px 이내

            const nearby = shapes
              .filter(s => s.type !== 'arrow' && s.renderMode === 'html')
              .filter(s => {
                // 객체의 바운딩 박스 계산
                let minX, maxX, minY, maxY;

                if (s.radius) {
                  // 원형
                  minX = s.x;
                  maxX = s.x + s.radius * 2;
                  minY = s.y;
                  maxY = s.y + s.radius * 2;
                } else {
                  // 사각형
                  minX = s.x;
                  maxX = s.x + (s.width || 100);
                  minY = s.y;
                  maxY = s.y + (s.height || 100);
                }

                // 객체 내부에 있는지 확인
                const isInside = canvasX >= minX && canvasX <= maxX && canvasY >= minY && canvasY <= maxY;

                // 마진 영역에 있는지 확인
                const isInMargin = canvasX >= minX - proximityMargin &&
                  canvasX <= maxX + proximityMargin &&
                  canvasY >= minY - proximityMargin &&
                  canvasY <= maxY + proximityMargin;

                // 둘레에서 proximityMargin만큼 확장한 영역 안에 있는지 확인
                return isInMargin; // 경계 밖 50px 이내 OR 객체 내부
              })
              .map(s => s.id);

            nearbyShapeIdsRef.current = nearby; // ref에 즉시 저장

            // state 변경되었을 때만 업데이트 (불필요한 리렌더링 방지)
            if (nearby.length !== nearbyShapeIds.length || nearby.some((id, i) => id !== nearbyShapeIds[i])) {
              setNearbyShapeIds(nearby);
            }
          } else if (currentTool !== 'arrow') {
            nearbyShapeIdsRef.current = [];
            setNearbyShapeIds([]);
          }

          // 가짜 Konva 이벤트 객체 생성
          const fakeEvent = {
            evt: e.nativeEvent,
            cancelBubble: false,
            target: {
              getStage: () => ({
                getPointerPosition: () => ({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top
                }),
                scaleX: () => stageScale,
                scaleY: () => stageScale,
                x: () => stagePos.x,
                y: () => stagePos.y,
                container: () => canvasContainerRef.current
              })
            }
          };
          handleStageMouseMove(fakeEvent);

          // HTML 모드에서 화살표 그리는 중 끝점 업데이트
          if (isConnecting && currentShape && currentShape.type === 'arrow' && currentShape.renderMode === 'html') {
            const rect = canvasContainerRef.current?.getBoundingClientRect();
            if (rect) {
              const canvasX = (e.clientX - rect.left - stagePos.x) / stageScale;
              const canvasY = (e.clientY - rect.top - stagePos.y) / stageScale;

              // 연결점 스냅
              const nearestPoint = findNearestConnectionPoint(canvasX, canvasY, shapes, 20);

              let finalX = canvasX;
              let finalY = canvasY;

              if (nearestPoint) {
                finalX = nearestPoint.x;
                finalY = nearestPoint.y;
                setSnappedPoint(nearestPoint);
              } else {
                setSnappedPoint(null);
              }

              // 미리보기 업데이트
              setCurrentShape(prev => {
                if (!prev) return null;

                const startX = prev.x || 0;
                const startY = prev.y || 0;
                const arrowWidth = finalX - startX;
                const arrowHeight = finalY - startY;

                // 곡선인 경우 웨이포인트 계산
                let waypoints: { x: number; y: number }[] | undefined;
                if (prev.isCurved) {
                  const midX = startX + arrowWidth / 2;
                  const midY = startY + arrowHeight / 2;
                  const dx = arrowWidth;
                  const dy = arrowHeight;
                  const distance = Math.sqrt(dx * dx + dy * dy);
                  const offsetRatio = 0.2;

                  if (distance > 0) {
                    const perpX = -dy / distance;
                    const perpY = dx / distance;
                    const offset = distance * offsetRatio;
                    waypoints = [{ x: midX + perpX * offset, y: midY + perpY * offset }];
                  } else {
                    waypoints = [{ x: midX, y: midY }];
                  }
                }

                return {
                  ...prev,
                  width: arrowWidth,
                  height: arrowHeight,
                  waypoints,
                };
              });
            }
          }
        }}
        onClick={(e) => {
          // canvas-container 자체를 클릭했는지 확인 (자식 요소가 아닌)
          if (e.target !== canvasContainerRef.current) return;

          // HTML 모드에서 화살표 완료
          if (isConnecting && currentShape && currentShape.type === 'arrow' && currentShape.renderMode === 'html') {
            const rect = canvasContainerRef.current?.getBoundingClientRect();
            if (rect) {
              const canvasX = (e.clientX - rect.left - stagePos.x) / stageScale;
              const canvasY = (e.clientY - rect.top - stagePos.y) / stageScale;

              // 끝점 스냅
              const nearestPoint = findNearestConnectionPoint(canvasX, canvasY, shapes, 20);

              let endX = canvasX;
              let endY = canvasY;
              let endShapeId: string | undefined;
              let endConnectionPoint: 'top' | 'right' | 'bottom' | 'left' | undefined;
              let endRelativeX: number | undefined;
              let endRelativeY: number | undefined;

              if (nearestPoint) {
                endX = nearestPoint.x;
                endY = nearestPoint.y;
                endShapeId = nearestPoint.shapeId;
                endConnectionPoint = nearestPoint.pointType;
              } else {
                const shapeAtPoint = findShapeAtPoint(canvasX, canvasY, shapes.filter(s => s.type !== 'arrow'));
                if (shapeAtPoint) {
                  endShapeId = shapeAtPoint.id;
                  const relativePos = getRelativePosition(canvasX, canvasY, shapeAtPoint);
                  if (relativePos) {
                    endRelativeX = relativePos.relativeX;
                    endRelativeY = relativePos.relativeY;
                  }
                }
              }

              // 최소 거리 체크
              const arrowWidth = endX - (currentShape.x || 0);
              const arrowHeight = endY - (currentShape.y || 0);
              const distance = Math.sqrt(arrowWidth * arrowWidth + arrowHeight * arrowHeight);

              if (distance >= 5) {
                const completedArrow: Shape = {
                  ...currentShape as Shape,
                  id: Date.now().toString(),
                  width: arrowWidth,
                  height: arrowHeight,
                  endShapeId,
                  endConnectionPoint,
                  endRelativeX,
                  endRelativeY,
                };

                setShapes(prev => {
                  const newShapes = [...prev, completedArrow];

                  // connectedArrows 업데이트
                  if (completedArrow.startShapeId) {
                    newShapes.forEach(shape => {
                      if (shape.id === completedArrow.startShapeId) {
                        if (!shape.connectedArrows) {
                          shape.connectedArrows = [];
                        }
                        shape.connectedArrows.push(completedArrow.id);
                      }
                    });
                  }

                  if (completedArrow.endShapeId) {
                    newShapes.forEach(shape => {
                      if (shape.id === completedArrow.endShapeId) {
                        if (!shape.connectedArrows) {
                          shape.connectedArrows = [];
                        }
                        shape.connectedArrows.push(completedArrow.id);
                      }
                    });
                  }

                  return newShapes;
                });
              }

              // 상태 초기화
              setCurrentShape(null);
              setIsConnecting(false);
              setShowConnectionPoints(false);
              setSnappedPoint(null);
              setCurrentTool('select');
            }
            return;
          }

          // HTML 모드에서 빈 공간 클릭 시 화살표 시작
          if (currentTool === 'arrow' && !isConnecting) {
            const rect = canvasContainerRef.current?.getBoundingClientRect();
            if (rect) {
              const canvasX = (e.clientX - rect.left - stagePos.x) / stageScale;
              const canvasY = (e.clientY - rect.top - stagePos.y) / stageScale;

              // 연결점 스냅
              const nearestPoint = findNearestConnectionPoint(canvasX, canvasY, shapes, 20);

              let startX = canvasX;
              let startY = canvasY;
              let startShapeId: string | undefined;
              let startConnectionPoint: 'top' | 'right' | 'bottom' | 'left' | undefined;
              let startRelativeX: number | undefined;
              let startRelativeY: number | undefined;

              if (nearestPoint) {
                startX = nearestPoint.x;
                startY = nearestPoint.y;
                startShapeId = nearestPoint.shapeId;
                startConnectionPoint = nearestPoint.pointType;
              }

              // 화살표는 항상 최상위 + 짝수 order
              const maxOrder = Math.max(...shapes.map(s => s.order ?? 0), 0);
              const newOrder = maxOrder % 2 === 0 ? maxOrder + 2 : maxOrder + 1;

              // 화살표 그리기 시작
              setIsConnecting(true);
              setShowConnectionPoints(true);
              setCurrentShape({
                id: 'temp-arrow',
                type: 'arrow',
                x: startX,
                y: startY,
                width: 0,
                height: 0,
                stroke: defaultArrowStroke,
                strokeWidth: defaultArrowStrokeWidth,
                isCurved: defaultIsCurved,
                startShapeId,
                startConnectionPoint,
                startRelativeX,
                startRelativeY,
                startArrow: defaultStartArrow,
                endArrow: defaultEndArrow,
                order: newOrder,
                renderMode: 'html',
              });
            }
          }
        }}
        onMouseUp={(e: React.MouseEvent) => {
          // 휠 버튼 놓을 때
          if (e.button === 1) {
            // RAF 취소
            if (canvasDragRafRef.current !== null) {
              cancelAnimationFrame(canvasDragRafRef.current);
            }

            setIsWheelPanning(false);
            setIsCanvasDragging(false);
            setCanvasDragStart(null);
            return;
          }

          const rect = canvasContainerRef.current?.getBoundingClientRect();
          if (!rect) return;

          const fakeEvent = {
            evt: e.nativeEvent,
            cancelBubble: false,
            target: {
              getStage: () => ({
                getPointerPosition: () => ({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top
                }),
                scaleX: () => stageScale,
                scaleY: () => stageScale,
                x: () => stagePos.x,
                y: () => stagePos.y,
                container: () => canvasContainerRef.current
              })
            }
          };
          handleStageMouseUp(fakeEvent);
        }}
        onMouseLeave={() => {
          // Cursor sync: send null cursor when leaving canvas
          if (isCollaborativeMode) {
            handleCursorLeave();
          }
          handleCanvasMouseLeave();
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onWheel={(e: React.WheelEvent) => {
          const rect = canvasContainerRef.current?.getBoundingClientRect();
          if (!rect) return;

          const fakeEvent = {
            evt: e.nativeEvent,
            cancelBubble: false,
            target: {
              getStage: () => ({
                scaleX: () => stageScale,
                scaleY: () => stageScale,
                x: () => stagePos.x,
                y: () => stagePos.y,
                container: () => canvasContainerRef.current,
                getPointerPosition: () => ({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top
                })
              })
            }
          };
          handleWheel(fakeEvent);
        }}
        onDoubleClick={(e: React.MouseEvent) => {
          const rect = canvasContainerRef.current?.getBoundingClientRect();
          if (!rect) return;

          // 빈 캔버스 더블클릭 시 테두리 없는 사각형 생성 및 편집 모드 진입
          if (e.target === canvasContainerRef.current && e.button === 0) {
            const canvasX = (e.clientX - rect.left - stagePos.x) / stageScale;
            const canvasY = (e.clientY - rect.top - stagePos.y) / stageScale;

            // 새 사각형의 기본 크기
            const defaultWidth = 200;
            const defaultHeight = 100;

            // order 계산 (항상 최상위)
            const maxOrder = Math.max(...shapes.map(s => s.order ?? 0), 0);
            const newOrder = maxOrder % 2 === 0 ? maxOrder + 2 : maxOrder + 1;

            // 테두리 없는 사각형 생성
            const newShapeId = Date.now().toString();
            const newShape: Shape = {
              id: newShapeId,
              type: 'rect',
              x: canvasX - defaultWidth / 2,
              y: canvasY - defaultHeight / 2,
              width: defaultWidth,
              height: defaultHeight,
              fill: '#ffffff',
              stroke: 'transparent',
              strokeWidth: 0,
              label: '',
              order: newOrder,
              renderMode: 'html',
              autoDeleteIfEmpty: true,
            };

            setShapes(prev => [...prev, newShape]);
            setSelectedShapes([newShapeId]);
            setShowResizeHandles(true);

            // 편집 모드 직접 진입 (shapes 상태 업데이트 전에 직접 설정)
            const textPadding = 8;
            const screenX = newShape.x * stageScale + stagePos.x;
            const screenY = newShape.y * stageScale + stagePos.y;
            const screenWidth = defaultWidth * stageScale;
            const screenHeight = defaultHeight * stageScale;

            setEditingShape(newShapeId);
            setEditingText('');
            setEditingPosition({
              x: rect.left + screenX + (textPadding * stageScale),
              y: rect.top + screenY + (textPadding * stageScale),
              width: screenWidth - (textPadding * 2 * stageScale),
              height: screenHeight - (textPadding * 2 * stageScale),
            });

            return;
          }

          const fakeEvent = {
            evt: e.nativeEvent,
            cancelBubble: false,
            target: {
              getStage: () => ({
                getPointerPosition: () => ({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top
                }),
                scaleX: () => stageScale,
                scaleY: () => stageScale,
                x: () => stagePos.x,
                y: () => stagePos.y,
                container: () => canvasContainerRef.current
              })
            }
          };
          handleStageDoubleClick(fakeEvent);
        }}
      >
        <div
          ref={toolbarRef}
          className="toolbar-container"
          style={{
            left: '10px',
          }}
        >
          <Toolbar
            currentTool={currentTool}
            onToolChange={handleToolChange}
            onSave={handleSave}
            onLoad={handleLoad}
            onExport={openExportModal}
            onImageConcat={() => setIsImageConcatModalOpen(true)}
            defaultStartArrow={defaultStartArrow}
            defaultEndArrow={defaultEndArrow}
            defaultIsCurved={defaultIsCurved}
            onDefaultStartArrowChange={setDefaultStartArrow}
            onDefaultEndArrowChange={setDefaultEndArrow}
            onDefaultIsCurvedChange={setDefaultIsCurved}
            defaultArrowStroke={defaultArrowStroke}
            defaultArrowStrokeWidth={defaultArrowStrokeWidth}
            onDefaultArrowStrokeChange={setDefaultArrowStroke}
            onDefaultArrowStrokeWidthChange={setDefaultArrowStrokeWidth}
            defaultFill={defaultFill}
            defaultStroke={defaultStroke}
            defaultStrokeWidth={defaultStrokeWidth}
            defaultFontSize={defaultFontSize}
            onDefaultFillChange={setDefaultFill}
            onDefaultStrokeChange={setDefaultStroke}
            onDefaultStrokeWidthChange={setDefaultStrokeWidth}
            onDefaultFontSizeChange={setDefaultFontSize}
            selectedShapes={shapes.filter(s => selectedShapes.includes(s.id))}
            onUpdateShapes={handleObjectUpdate}
          >
            <CollaborationPanel />
          </Toolbar>
        </div>

        {/* 올가미 선택 사각형 (HTML) */}
        {isLassoSelecting && lassoStart && lassoEnd && (() => {
          const x = Math.min(lassoStart.x, lassoEnd.x);
          const y = Math.min(lassoStart.y, lassoEnd.y);
          const width = Math.abs(lassoEnd.x - lassoStart.x);
          const height = Math.abs(lassoEnd.y - lassoStart.y);

          return (
            <div
              style={{
                position: 'absolute',
                left: stagePos.x + x * stageScale,
                top: stagePos.y + y * stageScale,
                width: width * stageScale,
                height: height * stageScale,
                border: '2px dashed #3B82F6',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                pointerEvents: 'none',
                zIndex: overlayZIndex,
              }}
            />
          );
        })()}

        {/* 빈 캔버스 플레이스홀더 */}
        {shapes.length === 0 && !isDragOver && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: '#9ca3af',
            pointerEvents: 'none',
            zIndex: 0,
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.4 }}>+</div>
            <div style={{ fontSize: '15px', fontWeight: 500 }}>
              {t.placeholder.line1}
            </div>
            <div style={{ fontSize: '13px', marginTop: '6px', opacity: 0.7 }}>
              {t.placeholder.line2}
            </div>
            <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.7 }}>
              {t.placeholder.line3}
            </div>
          </div>
        )}

        {/* HTML 기반 Shapes (order로 정렬하여 z-index 올바르게 적용) */}
        {shapes
          .filter(shape => shape.renderMode === 'html' && ['rect', 'circle', 'arrow', 'pdf', 'image', 'code', 'md', 'youtube', 'audio'].includes(shape.type))
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map(shape => {
            const commonProps = {
              key: shape.id,
              shape,
              isSelected: selectedShapes.includes(shape.id),
              showResizeHandles: showResizeHandles && selectedShapes.length === 1 && selectedShapes.includes(shape.id),
              stagePos,
              stageScale,
              isDrawing: isDrawing || isConnecting || isLassoSelecting,
              isEditing: editingShape === shape.id,
              currentTool,
              onDragPrepare: handleDragPrepare,
              onDragStart: handleDragStart,
              onDragMove: (shapeId: string, newX: number, newY: number) => {
                handleDragMove(shapeId, newX, newY);
                updateEditorPosition(shapeId, newX, newY);
              },
              onDragEnd: handleDragEnd,
              onResizeStart: handleShapeResizeStart,
              onResize: (shapeId: string, newWidth: number, newHeight: number, newX?: number, newY?: number) => {
                handleShapeResize(shapeId, newWidth, newHeight, newX, newY);
                const finalX = newX ?? shapes.find(s => s.id === shapeId)?.x ?? 0;
                const finalY = newY ?? shapes.find(s => s.id === shapeId)?.y ?? 0;
                updateEditorPosition(shapeId, finalX, finalY, newWidth, newHeight);
              },
              onResizeEnd: handleShapeResizeEnd,
              onClick: handleShapeClick,
              onDoubleClick: handleDoubleClickToEdit,
              onContextMenu: (shapeId: string, e: React.MouseEvent) => {
                setContextMenu({
                  x: e.clientX,
                  y: e.clientY,
                  shapeId,
                });
              },
              onMouseEnter: (shapeId: string) => {
                if (currentTool === 'arrow' || isConnecting) {
                  setHoveredShapeId(shapeId);
                }
              },
              onMouseLeave: () => {
                if (currentTool === 'arrow' || isConnecting) {
                  setHoveredShapeId(null);
                }
              },
              onStageClick: (canvasX: number, canvasY: number) => handleArrowStageClick(canvasX, canvasY),
            };

            if (shape.type === 'rect') {
              return <HTMLRectangle {...commonProps} />;
            } else if (shape.type === 'circle') {
              return <HTMLCircle {...commonProps} />;
            } else if (shape.type === 'pdf') {
              return (
                <HTMLPDF
                  {...commonProps}
                  onShapeUpdate={handleShapeUpdate}
                  onExtractPage={handleExtractPage}
                  onAnnotationUpdate={handleAnnotationUpdate}
                />
              );
            } else if (shape.type === 'image') {
              return <HTMLImage {...commonProps} onAnnotationUpdate={handleAnnotationUpdate} />;
            } else if (shape.type === 'code') {
              return <HTMLCodeBlock {...commonProps} />;
            } else if (shape.type === 'md') {
              return <HTMLMarkdown {...commonProps} />;
            } else if (shape.type === 'youtube') {
              // 드래그 중이면 (자기 자신 포함) iframe 비활성화하여 드래그 상태 유지
              return <HTMLYoutube {...commonProps} isAnyDragging={draggingShapeId !== null} isDragging={draggingShapeId === shape.id} />;
            } else if (shape.type === 'arrow') {
              return (
                <SVGArrow
                  key={shape.id}
                  shape={shape}
                  isSelected={selectedShapes.includes(shape.id)}
                  showEndpointHandles={selectedShapes.includes(shape.id) && selectedShapes.length === 1}
                  stagePos={stagePos}
                  stageScale={stageScale}
                  isDrawing={isDrawing || isConnecting || isLassoSelecting}
                  currentTool={currentTool}
                  allShapes={shapes}
                  onClick={(shapeId, e) => {
                    if (e.ctrlKey || e.metaKey) {
                      setSelectedShapes(prev =>
                        prev.includes(shapeId)
                          ? prev.filter(id => id !== shapeId)
                          : [...prev, shapeId]
                      );
                    } else {
                      setSelectedShapes([shapeId]);
                    }
                  }}
                  onContextMenu={handleArrowContextMenu}
                  onDragStart={handleArrowDragStart}
                  onDragMove={handleArrowDragMove}
                  onDragEnd={handleArrowDragEnd}
                  onStartPointMove={handleStartPointMove}
                  onEndPointMove={handleEndPointMove}
                  onStartPointEnd={handleStartPointEnd}
                  onEndPointEnd={handleEndPointEnd}
                  onSnapUpdate={handleSnapUpdate}
                  onEndpointDraggingChange={handleEndpointDraggingChange}
                  onCurveControlMove={handleCurveControlMove}
                  onWaypointMove={handleWaypointMove}
                  onWaypointAdd={handleWaypointAdd}
                  onWaypointDelete={handleWaypointDelete}
                />
              );
            } else if (shape.type === 'audio') {
              return <HTMLAudio {...commonProps} />;
            }

            return null;
          })}


        {/* 그리는 중 미리보기 (HTML 모드) */}
        {isDrawing && currentShape && currentShape.renderMode === 'html' && (
          <>
            {currentShape.type === 'rect' && (
              <HTMLRectangle
                key="temp-rect-preview"
                shape={currentShape as Shape}
                isSelected={false}
                stagePos={stagePos}
                stageScale={stageScale}
                isDrawing={true}
                currentTool={currentTool}
              />
            )}
            {currentShape.type === 'circle' && (
              <HTMLCircle
                key="temp-circle-preview"
                shape={currentShape as Shape}
                isSelected={false}
                stagePos={stagePos}
                stageScale={stageScale}
                isDrawing={true}
                currentTool={currentTool}
              />
            )}
            {currentShape.type === 'code' && (
              <HTMLCodeBlock
                key="temp-code-preview"
                shape={currentShape as Shape}
                isSelected={false}
                stagePos={stagePos}
                stageScale={stageScale}
                isDrawing={true}
                currentTool={currentTool}
              />
            )}
            {currentShape.type === 'md' && (
              <HTMLMarkdown
                key="temp-md-preview"
                shape={currentShape as Shape}
                isSelected={false}
                stagePos={stagePos}
                stageScale={stageScale}
                isDrawing={true}
                currentTool={currentTool}
              />
            )}
          </>
        )}

        {/* 화살표 그리는 중 미리보기 (HTML 모드) */}
        {isConnecting && currentShape && currentShape.type === 'arrow' && currentShape.renderMode === 'html' && (
          <SVGArrow
            key="temp-arrow-preview"
            shape={currentShape as Shape}
            isSelected={false}
            showEndpointHandles={false}
            stagePos={stagePos}
            stageScale={stageScale}
            isDrawing={false}
            currentTool={currentTool}
            allShapes={shapes}
          />
        )}

        {/* HTML shapes의 연결점 표시 - 화살표 도구이거나, 연결 중이거나, 끝점 드래그 중일 때 항상 표시 */}
        {(() => {
          const shouldShow = currentTool === 'arrow' || isConnecting || isDraggingArrowEndpoint;

          return shouldShow ? shapes
            .filter(shape => shape.renderMode === 'html' && ['rect', 'circle', 'pdf', 'image', 'youtube'].includes(shape.type))
            .map(shape => (
              <ConnectionPoints
                key={`connection-${shape.id}`}
                shape={shape}
                stagePos={stagePos}
                stageScale={stageScale}
                show={true}
                snappedPoint={snappedPoint}
              />
            )) : null;
        })()}

        {/* 스냅 가이드라인 - 드래그 중일 때만 표시 */}
        {draggingShapeId && alignmentGuides.length > 0 && (
          <AlignmentGuides
            guides={alignmentGuides}
            stagePos={stagePos}
            stageScale={stageScale}
            canvasWidth={width}
            canvasHeight={height}
          />
        )}

        {/* 미니맵 */}
        <Minimap
          shapes={shapes}
          canvasSize={canvasSize}
          canvasBounds={canvasBounds}
          viewportSize={{ width, height }}
          stagePos={stagePos}
          stageScale={stageScale}
          onViewportChange={(newPos) => setStagePos(newPos)}
        />

        <HelpPanel />

        {/* Zoom to Fit 버튼 */}
        {shapes.length > 0 && (
          <button
            onClick={() => {
              const padding = 60;
              const { minX, minY, maxX, maxY } = canvasBounds;
              const contentWidth = maxX - minX;
              const contentHeight = maxY - minY;
              if (contentWidth <= 0 || contentHeight <= 0) return;
              const scale = Math.min(
                (width - padding * 2) / contentWidth,
                (height - padding * 2) / contentHeight,
                3
              );
              const centerX = (minX + maxX) / 2;
              const centerY = (minY + maxY) / 2;
              setStageScale(Math.max(scale, 0.1));
              setStagePos({
                x: width / 2 - centerX * scale,
                y: height / 2 - centerY * scale,
              });
            }}
            style={{
              position: 'fixed',
              top: 240,
              right: 220,
              width: 30,
              height: 30,
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              backgroundColor: 'rgba(255,255,255,0.95)',
              color: '#64748b',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
            title="Zoom to Fit (Ctrl+0)"
          >
            ⊞
          </button>
        )}

        {/* Remote user cursors (collaborative mode only) */}
        {isCollaborativeMode && remoteCursors.length > 0 && (
          <UserCursors
            cursors={remoteCursors}
            stagePos={stagePos}
            stageScale={stageScale}
            viewportWidth={width}
            viewportHeight={height}
          />
        )}

        {/* Selection overlay for other users (collaborative mode only) */}
        {isCollaborativeMode && (
          <SelectionOverlay
            shapes={shapes}
            remoteUsers={collaborationState.users}
            lockedShapes={lockedShapes}
            currentUserId={collaborationState.currentUser?.id}
            stagePos={stagePos}
            stageScale={stageScale}
          />
        )}
      </div>

      <InlineEditorOverlay
        editingShape={editingShape}
        editingText={editingText}
        editingPosition={editingPosition}
        editingMarkdown={editingMarkdown}
        editingMarkdownPosition={editingMarkdownPosition}
        editingCode={editingCode}
        editingCodeLanguage={editingCodeLanguage}
        editingCodePosition={editingCodePosition}
        shapes={shapes}
        stageScale={stageScale}
        overlayZIndex={overlayZIndex}
        setEditingText={setEditingText}
        setEditingMarkdown={setEditingMarkdown}
        setEditingCode={setEditingCode}
        setEditingCodeLanguage={setEditingCodeLanguage}
        onComplete={handleTextEditComplete}
        onCancel={handleTextEditCancel}
      />

      {
        showPDFViewer && (
          <PDFViewer
            file={pdfFile}
            onClose={() => setShowPDFViewer(false)}
          />
        )
      }

      {/* 코드 에디터 모달 */}
      {
        editingCodeShapeId && (() => {
          const codeShape = shapes.find(s => s.id === editingCodeShapeId);
          return codeShape ? (
            <CodeEditorModal
              isOpen={isCodeEditorOpen}
              code={codeShape.code || '// 코드를 입력하세요'}
              language={codeShape.language || 'javascript'}
              onSave={handleCodeSave}
              onClose={handleCodeEditorClose}
            />
          ) : null;
        })()
      }

      {/* 마크다운 에디터 모달 */}
      {
        editingMarkdownShapeId && (() => {
          const markdownShape = shapes.find(s => s.id === editingMarkdownShapeId);
          return markdownShape ? (
            <MarkdownEditorModal
              isOpen={isMarkdownEditorOpen}
              markdown={markdownShape.markdown || '# Markdown'}
              onSave={handleMarkdownSave}
              onClose={handleMarkdownEditorClose}
            />
          ) : null;
        })()
      }

      {/* 컨텍스트 메뉴 - 툴바 속성 표시 */}
      {contextMenu && selectedShapes.length > 0 && (() => {
        const contextShape = shapes.find(s => s.id === contextMenu.shapeId);
        if (!contextShape) return null;
        return (
          <ContextMenuPanel
            contextMenu={contextMenu}
            contextShape={contextShape}
            overlayZIndex={overlayZIndex}
            onUpdate={handleObjectUpdate}
            onDelete={() => {
              setShapes(prev => prev.filter(s => s.id !== contextMenu.shapeId));
              setContextMenu(null);
            }}
            onClose={() => setContextMenu(null)}
          />
        );
      })()}

      {/* 내보내기 모달 */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={closeExportModal}
        onExport={handleExport}
        hasSelection={selectedShapes.length > 0}
        progress={exportProgress}
      />

      {/* 이미지 연결 모달 */}
      <ImageConcatModal
        isOpen={isImageConcatModalOpen}
        onClose={() => setIsImageConcatModalOpen(false)}
        onConfirm={handleImageConcatConfirm}
      />

    </>
  );
};

export default Canvas;
